// Accounting-system connector (migration-service). QuickBooks Online today;
// the API is provider-scoped so Xero, Sage, Odoo and Dynamics slot in without
// a second client.
//
// Phase 1–2: connect an organisation, read its chart of accounts, and map
// payroll's logical account codes onto it. NOTHING here posts — the posting
// pipeline is a later phase, so this client deliberately has no post/retry
// call. See docs/architecture/QUICKBOOKS_ONLINE_ACCOUNTING_INTEGRATION.md.
import { gatewayApi } from '@/lib/hrApi';

/** The provider slug in the URL. One connector per provider, same shape. */
export type AccountingProvider = 'quickbooks';

const base = (provider: AccountingProvider) => `/api/v1/integrations/accounting/${provider}`;

/**
 * Accounting is configured PER LEGAL ENTITY, not per workspace.
 *
 * A group runs several companies, each with its own chart, its own mappings and
 * its own ledger — the same concept can map to different accounts in each. So
 * every call that reads or writes configuration says which company it means,
 * and the server refuses rather than guessing: showing one company's mappings
 * under another's name is worse than an error.
 */
const forEntity = (legalEntityId: string) => ({ params: { legalEntityId } });

export interface AccountingStatus {
  /** The provider's human label, e.g. "QuickBooks Online". */
  label?: string;
  /** Whether THIS environment carries Intuit app credentials at all. */
  /** The DEPLOYMENT holds credentials. Says nothing about this tenant — never
   *  gate a screen on it. */
  configured: boolean;
  /** THIS TENANT may connect: credentials, an open catalog row, and the launch
   *  allowlist all agree. This is the one to gate on. Optional so an older
   *  server (which never sent it) degrades to hidden rather than open. */
  offered?: boolean;
  connected: boolean;
  /** pending_activation: authorized at the provider but not yet activated by
   *  somebody with the permission. The OAuth callback is unauthenticated and
   *  cannot check that, so it leaves the connection unusable until an
   *  authenticated request activates it. */
  state?: 'connected' | 'needs_reauth' | 'revoked' | 'disconnected' | 'pending_activation';
  external_org_id?: string;
  environment?: 'sandbox' | 'production';
  organisation_name?: string;
  home_currency?: string;
  multicurrency_enabled: boolean;
  accounts_synced_at?: string | null;
  account_count: number;
  needs_reauth: boolean;
  reauth_by?: string | null;
  last_error?: string;
  connected_at?: string | null;
}

/** One company the caller may see on the Accounting tab, with its own ledger
 *  connection. The server lists ONLY companies inside the caller's
 *  accounting:read reach; `can_map` says whether their accounting:map reach
 *  covers it too. */
export interface AccountingCompany {
  legal_entity_id: string;
  name: string;
  connected: boolean;
  ledger_company_name?: string;
  needs_reauth: boolean;
  last_error?: string;
  can_map: boolean;
}

export interface AccountingCompanyList {
  companies: AccountingCompany[];
  /** Connect / finish connecting / disconnect need reach over EVERY company
   *  (the owner, today). A single-company controller gets false. The route
   *  permission (migration:admin) is still required on top. */
  can_manage_connection: boolean;
  /** The tenant limits accounting by company, so this is the caller's reach,
   *  not the whole workspace. */
  scoped: boolean;
}

/** The companies this caller may work with — the selector's source. Never
 *  build the selector from the payroll company list: that is a different
 *  permission's reach. */
export const getAccountingCompanies = (p: AccountingProvider) =>
  gatewayApi.get<AccountingCompanyList>(`${base(p)}/companies`).then((r) => ({
    companies: r.data?.companies ?? [],
    can_manage_connection: Boolean(r.data?.can_manage_connection),
    scoped: Boolean(r.data?.scoped),
  }));

export interface ExternalAccount {
  ID: string;
  Name: string;
  FullyQualifiedName: string;
  AccountType: string;
  AccountSubType: string;
  Classification: string;
  Currency: string;
  Active: boolean;
}

/** Why a mapping row cannot be posted with. Empty/absent = usable. */
export type MappingProblem = 'missing' | 'unknown_account' | 'inactive' | 'currency_mismatch';

export interface MappingRow {
  logical_code: string;
  label?: string;
  gl_code?: string;
  dr_cr?: string;
  source?: string;
  required: boolean;
  mapped: boolean;
  external_account_id?: string;
  external_account_name?: string;
  external_account_type?: string;
  external_account_currency?: string;
  problem?: MappingProblem;
}

export interface MappingView {
  external_org_id: string;
  rows: MappingRow[];
  /** False when payroll's account catalog could not be read — `ready` is then never true. */
  catalog_available: boolean;
  ready: boolean;
  missing_codes: string[];
  problem_codes: string[];
  home_currency?: string;
  accounts_count: number;
}

/** Status answers one of two questions.
 *
 *  WITH a company: that company's connection — name, currency, environment,
 *  whether it can post.
 *
 *  WITHOUT one: may this workspace use accounting at all, and is anything
 *  connected? No company fields come back, because there is no company in the
 *  question. The Finance tab gate asks this and has no entity to offer.
 *
 *  The other three below are per company always: they used to read the
 *  tenant's newest connection, which showed and acted on the wrong one in a
 *  group (O-10). */
export const getAccountingStatus = (p: AccountingProvider, legalEntityId?: string) =>
  gatewayApi
    .get<AccountingStatus>(`${base(p)}/status`, legalEntityId ? forEntity(legalEntityId) : undefined)
    .then((r) => r.data);

export const getExternalAccounts = (p: AccountingProvider, legalEntityId: string) =>
  gatewayApi
    .get<{ accounts: ExternalAccount[] }>(`${base(p)}/accounts`, forEntity(legalEntityId))
    .then((r) => r.data.accounts ?? []);

export const refreshExternalAccounts = (p: AccountingProvider, legalEntityId: string) =>
  gatewayApi
    .post<{ accounts: number }>(`${base(p)}/accounts/refresh`, {}, forEntity(legalEntityId))
    .then((r) => r.data.accounts);

export const getAccountMappings = (p: AccountingProvider, legalEntityId: string) =>
  gatewayApi.get<MappingView>(`${base(p)}/mappings`, forEntity(legalEntityId)).then((r) => r.data);

/** Send only the rows that changed; an empty external_account_id clears one. */
export const setAccountMappings = (
  p: AccountingProvider,
  legalEntityId: string,
  mappings: { logical_code: string; external_account_id: string }[],
) =>
  gatewayApi
    .put<MappingView>(`${base(p)}/mappings`, { mappings }, forEntity(legalEntityId))
    .then((r) => r.data);

/** Returns the provider's consent URL; the caller navigates the browser to it. */
/** The company the connection is FOR is decided here, on an authenticated
 *  request, and carried in the signed OAuth state — never in the callback
 *  query, which is unauthenticated. */
export const startAccountingOAuth = (p: AccountingProvider, legalEntityId: string, returnTo: string) =>
  gatewayApi
    .post<{ url: string }>(`${base(p)}/oauth/start`, { return_to: returnTo }, forEntity(legalEntityId))
    .then((r) => r.data.url);

/** Finish a connection the OAuth callback could only authorize.
 *
 *  This is where the actor's authority is actually checked: the callback has no
 *  token, so it cannot know whether the person still holds the permission.
 *  Idempotent — safe to fire on every return from the provider. */
export const activateAccountingOAuth = (p: AccountingProvider, legalEntityId: string) =>
  gatewayApi
    .post(`${base(p)}/oauth/activate`, {}, forEntity(legalEntityId))
    .then((r) => r.data);

export const disconnectAccounting = (p: AccountingProvider, legalEntityId: string) =>
  gatewayApi.post(`${base(p)}/disconnect`, {}, forEntity(legalEntityId)).then((r) => r.data);

// ── Posting (Phase 3) ────────────────────────────────────────────────────────
//
// An accountant presses Post. Nothing posts on a schedule and nothing posts on
// an event — that is Fern's rule (Q11), not a limitation.

/** unknown (audit D10): sent, but the ledger's answer was lost — it may be
 *  posted. Nothing re-sends it until a person resolves it (resolvePosting). */
export type PostingStatus = 'pending' | 'posted' | 'failed' | 'needs_redate' | 'unknown';

export interface Posting {
  id: string;
  source_kind: string;
  source_id: string;
  period_label: string;
  status: PostingStatus;
  doc_date?: string;
  currency?: string;
  total_amount?: string;
  external_doc_id?: string;
  external_doc_number?: string;
  attempts: number;
  last_error?: string;
  posted_at?: string | null;
  updated_at: string;
}

export interface PostOutcome {
  posting: Posting;
  /** The document was already posted; nothing was sent. Not an error. */
  already_posted?: boolean;
}

export const getPostings = (p: AccountingProvider, legalEntityId: string, limit = 50) =>
  gatewayApi
    .get<{ postings: Posting[] }>(`${base(p)}/postings`, { params: { limit, legalEntityId } })
    .then((r) => r.data.postings ?? []);

/** Post one approved payroll run's accrual journal. */
export const postPayrollRun = (p: AccountingProvider, runId: string) =>
  gatewayApi.post<PostOutcome>(`${base(p)}/postings/payroll/${runId}`, {}).then((r) => r.data);

// ── Expense + payroll posting previews ───────────────────────────────────────
//
// The company is NOT a parameter here. It comes from the document itself —
// payroll stamps the employing company on a report at submission, and a run has
// exactly one. A browser-supplied company could post one company's money into
// another's ledger, and the document already knows the answer.

/** Why a dimension has, or has not, a value. `unmapped` is the only one that
 *  blocks: `not_required` is a complete answer, not a missing one. */
export type DimensionStatus = 'resolved' | 'unmapped' | 'not_required';

export interface DimensionOutcome {
  dimension: string;
  status: DimensionStatus;
  external_value_id?: string;
  external_value_name?: string;
  /** Which source answered — "class came from the expense category". */
  decided_by?: string;
  source_kind?: string;
  source_id?: string;
  source_label?: string;
  reason?: string;
}

export interface PostingIssue {
  code: string;
  /** 1-based item number, or absent for the document as a whole. */
  line?: number;
  message: string;
  source_kind?: string;
  source_id?: string;
  source_label?: string;
  logical_code?: string;
  blocking: boolean;
}

export interface ExpenseLineDraft {
  line: number;
  item_id?: string;
  logical_code: string;
  external_account_id?: string;
  account_name?: string;
  dr_cr: string;
  amount: string;
  description?: string;
  class: DimensionOutcome;
  location: DimensionOutcome;
}

export interface ExpenseDraft {
  report_id: string;
  report_number?: string;
  legal_entity_id: string;
  company_name?: string;
  external_org_id?: string;
  doc_date: string;
  currency: string;
  description?: string;
  lines: ExpenseLineDraft[];
  total_dr: string;
  total_cr: string;
  /** Reported, never subtracted: recognition is gross. Shown so Finance sees
   *  how the claim was funded before posting it. */
  advance_covered?: string;
  issues: PostingIssue[];
  ready: boolean;
}

export const previewExpensePosting = (p: AccountingProvider, reportId: string) =>
  gatewayApi.get<ExpenseDraft>(`${base(p)}/postings/expense/${reportId}/preview`).then((r) => r.data);

export const postExpenseReport = (p: AccountingProvider, reportId: string) =>
  gatewayApi.post<PostOutcome>(`${base(p)}/postings/expense/${reportId}`, {}).then((r) => r.data);

// The CASH leg. A second posting for the same report, on the date the money
// moved — recognition says the company owes it, this says it has been paid.
// Pressed separately because a company can legitimately have posted one and
// not the other.
export const previewExpenseSettlement = (p: AccountingProvider, reportId: string) =>
  gatewayApi
    .get<ExpenseDraft>(`${base(p)}/postings/expense/${reportId}/settlement/preview`)
    .then((r) => r.data);

export const postExpenseSettlement = (p: AccountingProvider, reportId: string) =>
  gatewayApi
    .post<PostOutcome>(`${base(p)}/postings/expense/${reportId}/settlement`, {})
    .then((r) => r.data);

export interface PayrollDraft {
  run_id: string;
  legal_entity_id: string;
  entity_name?: string;
  provider: string;
  company_name?: string;
  external_org_id?: string;
  period_label?: string;
  reference?: string;
  description?: string;
  doc_date: string;
  currency: string;
  class_id?: string;
  total_dr: string;
  total_cr: string;
  issues: PostingIssue[];
  ready: boolean;
}

export const previewPayrollPosting = (p: AccountingProvider, runId: string) =>
  gatewayApi.get<PayrollDraft>(`${base(p)}/postings/payroll/${runId}/preview`).then((r) => r.data);

// ── Dimensions: bind a legal entity to a ledger class ────────────────────────
//
// Fern's payroll splits across four classes, and those four are legal
// entities. A payroll run belongs to exactly one entity, so one run posts one
// journal carrying one class — nothing is subtotalled by department.
//
// Absence means absence: a tenant with no binding posts with no dimension,
// exactly as before.

export type AccountingDimension = 'class' | 'location';

export interface DimensionValue {
  id: string;
  name: string;
  full_name?: string;
  active: boolean;
}

export interface DimensionMapping {
  dimension?: string;
  /** What is bound. 'legal_entity' today. */
  source_kind?: string;
  source_id: string;
  external_value_id: string;
  external_value_name?: string;
}

export const getDimensionValues = (
  p: AccountingProvider,
  legalEntityId: string,
  dimension: AccountingDimension,
) =>
  gatewayApi
    .get<{ values: DimensionValue[] }>(`${base(p)}/dimensions/${dimension}/values`, forEntity(legalEntityId))
    .then((r) => r.data.values ?? []);

export const getDimensionMappings = (
  p: AccountingProvider,
  legalEntityId: string,
  dimension: AccountingDimension,
) =>
  gatewayApi
    .get<{ mappings: DimensionMapping[] }>(`${base(p)}/dimensions/${dimension}/mappings`, forEntity(legalEntityId))
    .then((r) => r.data.mappings ?? []);

/** Send only what changed; an empty external_value_id clears a binding. */
export const setDimensionMappings = (
  p: AccountingProvider,
  legalEntityId: string,
  dimension: AccountingDimension,
  mappings: DimensionMapping[],
) =>
  gatewayApi
    .put<{ mappings: DimensionMapping[] }>(
      `${base(p)}/dimensions/${dimension}/mappings`,
      { mappings },
      forEntity(legalEntityId),
    )
    .then((r) => r.data.mappings ?? []);

// ── The entity's accounting policy ──────────────────────────────────────────
//
// Which sources decide a dimension and in what ORDER, and which real date a
// journal carries. These were compiled constants until the connector learned
// that one company's accounting answers are not every company's.
//
// Every policy the backend stores is configurable here now.

/** A thing a Class or Location can be decided from. The SET is the platform's;
 *  the order is the customer's. */
export type DimensionSourceKind = 'expense_category' | 'department' | 'branch' | 'legal_entity';

export interface EntityAccountingConfig {
  legal_entity_id: string;
  provider: string;
  /** Ordered, highest precedence first. [] means this company does not set
   *  the dimension at all — which is different from a source that applies
   *  with nothing mapped to it. */
  expense_class_source: DimensionSourceKind[];
  expense_location_source: DimensionSourceKind[];
  expense_date_basis: string;
  payroll_date_basis: string;
  advance_mode: string;
  closed_period_policy: string;
  /** R1: how contractor runs post — bill (default) | journal. */
  contractor_posting_mode?: string;
  /** Policy fields that are IN USE but not yet decided. Their values are the
   *  conservative ones, so nothing behaves differently — the point is that an
   *  unmade decision stays visible instead of becoming one by forgetting.
   *  Does not block posting. */
  provisional: string[];
  updated_at?: string;
}

/** One logical account the CHOSEN policy needs, and whether it is mapped.
 *  Policy-driven: an entity posting advances per employee needs bindings
 *  rather than a shared account, so that row has no logical code. */
export interface RequiredAccount {
  logical_code: string;
  label: string;
  why: string;
  mapped: boolean;
  external_account_name?: string;
}

export interface EntityPolicyOptions {
  dimension_sources: DimensionSourceKind[];
  expense_date_bases: string[];
  payroll_date_bases: string[];
  advance_modes: string[];
  closed_period_policies: string[];
  /** What this ledger actually has. A dimension missing here is not
   *  configurable, because nothing could be sent in it. */
  supported_dimensions: string[];
}

export interface EntityPolicyView {
  legal_entity_id: string;
  provider: string;
  company_name?: string;
  /** false when this company has not chosen yet — which is when posting
   *  refuses with ACCOUNTING_CONFIG_MISSING. */
  configured: boolean;
  config?: EntityAccountingConfig;
  options: EntityPolicyOptions;
  /** What the chosen policy needs mapped before the matching journals post. */
  required_accounts: RequiredAccount[];
}

export const getEntityAccountingConfig = (p: AccountingProvider, legalEntityId: string) =>
  gatewayApi
    .get<EntityPolicyView>(`${base(p)}/entity-config`, forEntity(legalEntityId))
    .then((r) => r.data);

export interface EntityAccountingConfigInput {
  expense_class_source: DimensionSourceKind[];
  expense_location_source: DimensionSourceKind[];
  expense_date_basis: string;
  payroll_date_basis: string;
  advance_mode: string;
  closed_period_policy: string;
  contractor_posting_mode?: string;
  /** Omit to keep the stored marks; [] means everything here is now
   *  confirmed. Never send [] unless somebody actually confirmed. */
  provisional?: string[];
}

export const setEntityAccountingConfig = (
  p: AccountingProvider,
  legalEntityId: string,
  input: EntityAccountingConfigInput,
) =>
  gatewayApi
    .put<EntityPolicyView>(`${base(p)}/entity-config`, input, forEntity(legalEntityId))
    .then((r) => r.data);

// ── Per-employee advance accounts ───────────────────────────────────────────
//
// For an entity whose advance policy is "each employee's own account". The
// employee id here is the AUTH USER id — the id an expense report carries, and
// the one the advance journal line resolves against. It is NOT the payroll
// roster's employee id; binding from that directory would store configuration
// keyed by an id nothing resolves.
//
// The rows are everyone who has claimed an expense for this company, merged
// with anyone already bound. Unbound people come back with an empty account.

export interface EmployeeAdvanceAccountRow {
  employee_id: string;
  employee_name?: string;
  external_account_id?: string;
  external_account_name?: string;
  external_account_type?: string;
  /** unknown_account | inactive. Empty means usable. */
  problem?: string;
}

export interface EmployeeAdvanceAccountsView {
  external_org_id: string;
  rows: EmployeeAdvanceAccountRow[];
  /** The active chart minus accounts already taken, so the UI cannot offer a
   *  duplicate — one account belongs to one person. */
  candidates: ExternalAccount[];
  problem_count: number;
}

export const getEmployeeAdvanceAccounts = (p: AccountingProvider, legalEntityId: string) =>
  gatewayApi
    .get<EmployeeAdvanceAccountsView>(`${base(p)}/employee-accounts`, forEntity(legalEntityId))
    .then((r) => r.data);

/** Send only what changed; an empty external_account_id clears a binding. */
export const setEmployeeAdvanceAccounts = (
  p: AccountingProvider,
  legalEntityId: string,
  accounts: { employee_id: string; employee_name?: string; external_account_id: string }[],
) =>
  gatewayApi
    .put<EmployeeAdvanceAccountsView>(`${base(p)}/employee-accounts`, { accounts }, forEntity(legalEntityId))
    .then((r) => r.data);

// ── Moving a journal the ledger refused ─────────────────────────────────────

/** Record the date a person chose after the ledger refused the policy's one.
 *
 *  This does NOT post. Pressing Post afterwards goes through the same
 *  preview-and-send path, so what reaches the ledger is still what was shown.
 *
 *  Server-side this rotates the idempotency key, because a re-dated document is
 *  a different document: re-sent under the original key, the provider's replay
 *  protection would return the first document and post nothing. */
export const redatePosting = (
  p: AccountingProvider,
  legalEntityId: string,
  kind: string,
  sourceId: string,
  docDate: string,
) =>
  gatewayApi
    .post<{ posting: Posting }>(
      `${base(p)}/postings/${kind}/${sourceId}/redate`,
      { doc_date: docDate },
      forEntity(legalEntityId),
    )
    .then((r) => r.data.posting);

/** Settle a posting whose send had an unknown outcome, after looking in the
 *  ledger (audit D10): `found` with the ledger's journal id, or `not_found`
 *  (postable again under a fresh key). */
export const resolvePosting = (
  p: AccountingProvider,
  legalEntityId: string,
  kind: 'payroll_accrual' | 'payroll_settlement' | 'expense_report' | 'expense_settlement',
  sourceId: string,
  outcome: 'found' | 'not_found',
  externalDocId = '',
) =>
  gatewayApi
    .post<{ posting: Posting }>(
      `${base(p)}/postings/${kind}/${sourceId}/resolve`,
      { outcome, external_doc_id: externalDocId },
      forEntity(legalEntityId),
    )
    .then((r) => r.data.posting);

/** True when a post call came back "sent, outcome unknown" (409). */
export const isOutcomeUnknown = (e: unknown): boolean =>
  (e as { response?: { status?: number; data?: { code?: string } } })?.response?.data?.code === 'outcome_unknown';

/** firstOfNextMonth suggests a day after the one the ledger refused.
 *
 *  A SUGGESTION in an editable field, not a computed "next open period":
 *  nothing here knows which periods a customer's ledger has open, and the
 *  person doing the moving does. Pure, so the suggestion is testable. */
export const firstOfNextMonth = (refused?: string | null): string => {
  const base = refused && /^\d{4}-\d{2}-\d{2}/.test(refused) ? new Date(`${refused.slice(0, 10)}T00:00:00Z`) : new Date();
  const y = base.getUTCFullYear();
  const m = base.getUTCMonth() + 1; // 0-indexed → the month after
  const next = new Date(Date.UTC(y, m, 1));
  return next.toISOString().slice(0, 10);
};

// ── The payroll cash leg ────────────────────────────────────────────────────
//
// The accrual says what is owed; this says it has been paid. NET_PAYABLE was
// credited by every accrual and debited by nothing, so the net-salary payable
// only ever grew. Dated when the money left — not by the configured payroll
// date basis, which governs the accrual.

export const previewPayrollSettlement = (p: AccountingProvider, runId: string) =>
  gatewayApi
    .get<PayrollDraft>(`${base(p)}/postings/payroll/${runId}/settlement/preview`)
    .then((r) => r.data);

export const postPayrollSettlement = (p: AccountingProvider, runId: string) =>
  gatewayApi
    .post<PostOutcome>(`${base(p)}/postings/payroll/${runId}/settlement`, {})
    .then((r) => r.data);


// ── Multi-entity G8: contractor payments → the paying company's ledger ───────
export interface ContractorLineDraft {
  logical_code: string;
  account_id: string;
  account_name?: string;
  dr_cr: 'DR' | 'CR';
  amount: string;
  description: string;
  payee_id: string;
  vendor_id?: string;
  vendor_name?: string;
}
export interface ContractorDraft {
  run_id: string;
  legal_entity_id: string;
  provider: string;
  company_name?: string;
  doc_date: string;
  currency: string;
  lines: ContractorLineDraft[];
  total_dr: string;
  total_cr: string;
  issues: PostingIssue[];
  ready: boolean;
  mode?: string;
  bills?: ContractorBillDraft[];
}
export interface LedgerVendor { id: string; display_name: string; active: boolean }

/** R1: one payee's accounts-payable documents (bill mode). */
export interface ContractorBillDraft {
  line_id: string;
  payee_id: string;
  payee_name: string;
  vendor_name?: string;
  gross: string;
  wht: string;
  net: string;
  bill_status: string;
  credit_status: string;
  payment_status: string;
}
export interface ContractorPaymentLine {
  line_id: string;
  payee_name: string;
  net: string;
  paid_on?: string;
  payment_status: string;
  posting: 'will_post' | 'posted' | 'not_eligible';
  reason?: string;
}
export interface ContractorPaymentDraft {
  run_id: string;
  mode: string;
  company_name?: string;
  currency: string;
  bank_account_name?: string;
  lines: ContractorPaymentLine[];
  issues: PostingIssue[];
  ready: boolean;
}
export const previewContractorPayment = (p: AccountingProvider, runId: string) =>
  gatewayApi.get<ContractorPaymentDraft>(`${base(p)}/postings/contractor/${runId}/payment/preview`).then((r) => r.data);
export const postContractorPayment = (p: AccountingProvider, runId: string) =>
  gatewayApi.post<PostOutcome>(`${base(p)}/postings/contractor/${runId}/payment`, {}).then((r) => r.data);

export const previewContractorPosting = (p: AccountingProvider, runId: string) =>
  gatewayApi.get<ContractorDraft>(`${base(p)}/postings/contractor/${runId}/preview`).then((r) => r.data);
export const postContractorRun = (p: AccountingProvider, runId: string) =>
  gatewayApi.post<PostOutcome>(`${base(p)}/postings/contractor/${runId}`, {}).then((r) => r.data);
export const listLedgerVendors = (p: AccountingProvider, legalEntityId: string) =>
  gatewayApi
    .get<{ vendors: LedgerVendor[] }>(`${base(p)}/vendors`, { params: { legalEntityId } })
    .then((r) => r.data.vendors ?? []);
export const bindPayeeVendor = (p: AccountingProvider, legalEntityId: string, b: {
  payee_id: string; payee_name: string; external_vendor_id: string; external_vendor_name: string;
}) =>
  gatewayApi
    .put(`${base(p)}/vendor-bindings`, { bindings: [b] }, { params: { legalEntityId } })
    .then((r) => r.data);
